/**
 * Imports each image of a glTF as Godot's texture importer imports the file it loads the image
 * from: the texel passes, the mip chain and the format its sidecar asks for.
 */
import { importDecodedImage } from '../image/textureProcessing';
import { gltfObjects } from '../../../godot/import/gltf';
import { copyGodotFormat } from '../../textures/sourceColorSpace';
import { dropMipChain, hasDroppedMipChain } from '../../textures/godotTextureFilter';
import { forEachSurfaceMaterial } from './surfaceMaterials';
/**
 * A plugin that imports each image source once, as the loader decodes it, so every texture that
 * samples it, through `EXT_texture_webp` too, clones the imported texture. A plugin has no hook per
 * image source, so it wraps the parser's `loadImageSource`, which clones a repeat request too.
 */
export function importingImages(imageImport) {
    return (parser) => {
        const loadImageSource = parser.loadImageSource.bind(parser);
        const imported = new Map();
        parser.loadImageSource = (sourceIndex, loader) => {
            const first = imported.get(sourceIndex);
            if (first)
                return first.then((texture) => texture.clone());
            const texture = importedImage(parser, sourceIndex, loadImageSource(sourceIndex, loader), imageImport);
            imported.set(sourceIndex, texture);
            return texture;
        };
        return { name: 'textscene_godot_image_import' };
    };
}
async function importedImage(parser, sourceIndex, decoded, imageImport) {
    const texture = await decoded;
    const image = gltfObjects(parser.json.images)[sourceIndex];
    // The processor packs every image into a buffer view, so none is named by URI here.
    if (typeof image?.bufferView !== 'number')
        return texture;
    const bytes = new Uint8Array((await parser.getDependency('bufferView', image.bufferView)));
    const mimeType = typeof image.mimeType === 'string' ? image.mimeType : undefined;
    const imageImported = await imageImport(sourceIndex, bytes, mimeType);
    return imageImported ? importDecodedImage(texture, imageImported) : texture;
}
/**
 * Writes again on each texture under `root` what the loader overwrites after the import. A clone
 * drops the colour-space pin of a format with no sRGB view: a repeat image source, a `texCoord`
 * above 0 and `KHR_texture_transform` each clone, and the loader then tags a colour map sRGB. The
 * sampler also turns mipmaps back on for an import with no mip chain.
 */
export function restoreImportedSampling(root) {
    forEachSurfaceMaterial(root, (material) => {
        for (const value of Object.values(material)) {
            if (value?.isTexture !== true)
                continue;
            copyGodotFormat(value, value);
            if (hasDroppedMipChain(value))
                dropMipChain(value);
        }
    });
}
//# sourceMappingURL=imageImport.js.map